import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Loader2, Lock, MailPlus, Sparkles, Trash2, UserCog } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError } from '../../api/client'
import { useB2BAuthStore } from '../../stores/useB2BAuthStore'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'

/**
 * Workspace members + pending invites management.
 *
 * Scope:
 *   - Owner / admin can list members, invite new ones, change roles, remove.
 *   - Other roles see read-only member list.
 *   - Invite link is shown after creation (no email worker yet — recruiter
 *     copy-pastes into their own preferred channel).
 */

const ROLE_LABEL = {
  owner: 'Owner',
  admin: 'Admin',
  recruiter: 'Recruiter',
  bar_raiser: 'Bar raiser',
}

const INVITE_ROLE_OPTIONS = [
  { value: 'recruiter',  label: 'Recruiter — full screening workflow access' },
  { value: 'bar_raiser', label: 'Bar raiser — independent calibration scorer' },
  { value: 'admin',      label: 'Admin — manage members + workspace (no billing)' },
]

const MEMBER_ROLE_OPTIONS = [
  { value: 'recruiter',  label: 'Recruiter' },
  { value: 'bar_raiser', label: 'Bar raiser' },
  { value: 'admin',      label: 'Admin' },
]

export default function MembersScreen() {
  const me = useB2BAuthStore((s) => s.member)
  const isAdmin = me?.role === 'owner' || me?.role === 'admin'

  const subscription = useB2BBillingStore((s) => s.subscription)
  const loadingSubscription = useB2BBillingStore((s) => s.loadingSubscription)
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)
  const planCode = subscription?.plan?.plan_code
  // Treat unknown plan as "not free" so admins on paid plans don't see a CTA
  // flash before the subscription loads; the form stays the safe default.
  const isFreePlan = planCode === 'free'
  const canInvite = isAdmin && !isFreePlan

  const [members, setMembers] = useState([])
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Invite form
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('recruiter')
  const [inviting, setInviting] = useState(false)
  const [lastInviteLink, setLastInviteLink] = useState('')

  const reload = async () => {
    setError('')
    try {
      const [m, inv] = await Promise.all([
        api.get('/api/b2b/workspaces/me/members'),
        canInvite ? api.get('/api/b2b/workspaces/me/invites') : Promise.resolve({ invites: [] }),
      ])
      setMembers(Array.isArray(m) ? m : [])
      setInvites(inv?.invites || [])
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not load members.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!subscription && !loadingSubscription) {
      loadSubscription()
    }
  }, [subscription, loadingSubscription, loadSubscription])

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite])

  const submitInvite = async (e) => {
    e.preventDefault()
    setError('')
    setLastInviteLink('')
    setInviting(true)
    try {
      const created = await api.post('/api/b2b/workspaces/me/invites', { email, role })
      if (created?.token) {
        const inviteUrl = `${window.location.origin}/b2b/invite/${created.token}`
        await copyToClipboard(inviteUrl)
        setLastInviteLink(inviteUrl)
      }
      setEmail('')
      setRole('recruiter')
      await reload()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not create invite.')
    } finally {
      setInviting(false)
    }
  }

  const updateRole = async (memberId, nextRole) => {
    setError('')
    try {
      await api.patch(`/api/b2b/workspaces/me/members/${memberId}`, { role: nextRole })
      await reload()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not update role.')
    }
  }

  const removeMember = async (memberId) => {
    if (!confirm('Remove this member from the workspace?')) return
    setError('')
    try {
      await api.delete(`/api/b2b/workspaces/me/members/${memberId}`)
      await reload()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not remove member.')
    }
  }

  const revokeInvite = async (inviteId) => {
    setError('')
    try {
      await api.delete(`/api/b2b/workspaces/me/invites/${inviteId}`)
      await reload()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not revoke invite.')
    }
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Fallback — most modern browsers permit this from a button click.
    }
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-4xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Settings</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-bold text-ink-1">
          Members & invites
        </h1>
        <p className="mt-2 text-sm text-ink-2 max-w-2xl">
          {isFreePlan
            ? 'Your Free workspace runs on a single seat. Upgrade to add recruiters, bar raisers, and admins.'
            : 'Add recruiters, bar_raisers, and admins to your workspace. Bar raisers independently score screenings to feed the calibration metric — invite at least one to enable variance tracking.'}
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Upgrade CTA for Free plan owners/admins — invite flow needs paid seats */}
        {isAdmin && isFreePlan && (
          <div className="mt-8 bg-white rounded-3xl shadow-card border border-black/5 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-accent" />
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Team invites — paid plans only
              </p>
            </div>
            <h2 className="text-xl font-bold text-ink-1">
              Invite teammates by upgrading your workspace
            </h2>
            <p className="mt-2 text-sm text-ink-2 max-w-2xl">
              Free workspaces are limited to a single seat (you). Upgrade to
              Starter, Agency, or Scale to add recruiters, bar raisers, and
              admins to your team.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to="/b2b/pricing"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold rounded-2xl px-5 py-3"
              >
                <Sparkles className="w-4 h-4" />
                See plans
              </Link>
              <Link
                to="/b2b/billing"
                className="inline-flex items-center text-sm font-bold text-ink-2 hover:text-ink-1"
              >
                View current plan →
              </Link>
            </div>
          </div>
        )}

        {/* Invite form */}
        {canInvite && (
          <form onSubmit={submitInvite} className="mt-8 bg-white rounded-3xl shadow-card border border-black/5 p-6">
            <div className="flex items-center gap-2 mb-4">
              <MailPlus className="w-4 h-4 text-accent" />
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Invite a member
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="email"
                required
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={inviting}
                className="text-[15px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition sm:col-span-1"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={inviting}
                className="text-[14px] outline-none bg-white border border-black/10 rounded-2xl px-4 py-3 focus:border-accent transition sm:col-span-1"
              >
                {INVITE_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={inviting || !email}
                className="bg-accent hover:bg-accent-hover text-white font-bold rounded-2xl px-5 py-3 disabled:opacity-50"
              >
                {inviting ? 'Sending…' : 'Invite'}
              </button>
            </div>
            <p className="mt-3 text-[11px] text-ink-3">
              Invites expire after 14 days. The invitee creates a B2B
              account when accepting.
            </p>
            {lastInviteLink && (
              <p className="mt-2 text-xs text-emerald-700">
                Invite link copied below — share it with the invitee.
              </p>
            )}
          </form>
        )}

        {/* Members list */}
        <div className="mt-8 bg-white rounded-3xl shadow-card border border-black/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-black/5 flex items-center gap-2">
            <UserCog className="w-4 h-4 text-accent" />
            <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
              Members ({members.length})
            </p>
          </div>
          {loading ? (
            <div className="px-6 py-8 flex items-center gap-3 text-sm text-ink-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-3 border-b border-black/5">
                  <th className="py-2.5 px-6">Email</th>
                  <th className="py-2.5 px-3">Name</th>
                  <th className="py-2.5 px-3">Role</th>
                  {canInvite && <th className="py-2.5 px-3 text-right pr-6">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-black/5 last:border-0">
                    <td className="py-3 px-6 font-bold text-ink-1">{m.email}</td>
                    <td className="py-3 px-3 text-ink-2">{m.name || '—'}</td>
                    <td className="py-3 px-3">
                      {m.role === 'owner' || m.id === me?.id || !canInvite ? (
                        <span className="text-ink-2">{ROLE_LABEL[m.role] || m.role}</span>
                      ) : (
                        <select
                          value={m.role}
                          onChange={(e) => updateRole(m.id, e.target.value)}
                          className="text-[13px] outline-none bg-white border border-black/10 rounded-xl px-2.5 py-1.5 focus:border-accent transition"
                        >
                          {MEMBER_ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    {canInvite && (
                      <td className="py-3 px-3 text-right pr-6">
                        {m.role !== 'owner' && m.id !== me?.id && (
                          <button
                            type="button"
                            onClick={() => removeMember(m.id)}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-bold"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pending invites */}
        {canInvite && invites.length > 0 && (
          <div className="mt-6 bg-white rounded-3xl shadow-card border border-black/5 overflow-hidden">
            <div className="px-6 py-4 border-b border-black/5">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Invites
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-3 border-b border-black/5">
                  <th className="py-2.5 px-6">Email</th>
                  <th className="py-2.5 px-3">Role</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Expires</th>
                  <th className="py-2.5 px-3 text-right pr-6"> </th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const expiresStr = new Date(inv.expires_at).toLocaleDateString()
                  const inviteUrl = inv.token
                    ? `${window.location.origin}/b2b/invite/${inv.token}`
                    : null
                  return (
                    <tr key={inv.id} className="border-b border-black/5 last:border-0">
                      <td className="py-3 px-6 text-ink-1">{inv.email}</td>
                      <td className="py-3 px-3 text-ink-2">{ROLE_LABEL[inv.role] || inv.role}</td>
                      <td className="py-3 px-3 text-ink-2 capitalize">{inv.status}</td>
                      <td className="py-3 px-3 text-ink-3">{expiresStr}</td>
                      <td className="py-3 px-3 text-right pr-6">
                        <div className="inline-flex items-center gap-3">
                          {inviteUrl && inv.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(inviteUrl)}
                              className="inline-flex items-center gap-1 text-ink-2 hover:text-ink-1 text-xs font-bold"
                              aria-label="Copy invite link"
                              title={inviteUrl}
                            >
                              <Copy className="w-3.5 h-3.5" />
                              Copy link
                            </button>
                          )}
                          {inv.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => revokeInvite(inv.id)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-bold"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  )
}
